import React, {useEffect, useRef, useState} from "react";
import {FormInput, FormDropDown} from "src/view/style/modelTraining/form_input_styles";
import {KatButton, KatFlashbar, KatLabel, KatRadiobutton, KatSpinner} from "@amzn/katal-react";
import {INSTANCE_TYPES} from "src/view/modelTraining/experiments/instance_types";
import {useParams} from "react-router-dom";
import {isEmpty, setIfMounted} from "src/utils/common_utils";
import {
    EXPERIMENT_FETCH_FAILED_MESSAGE,
    FIELDS_REQUIRED_MESSAGE,
    KernelType,
} from "src/view/modelTraining/constants";
import {useDispatch, useSelector} from "react-redux";
import {usernameSelector} from "src/control/selectors/commons/user_selectors";
import {
    editExperimentSelector,
    fetchedExperimentSelector
} from "src/control/selectors/modelTraining/model_training_selectors";
import {loadExperimentResult, updateExperiment} from "src/control/actions/modelTraining/experiment_actions";
import {START_UPDATE_EXPERIMENT_LOADER} from "src/control/actions/action_types";


const EditExperiment = () => {
    const params: any = useParams()
    const dispatch = useDispatch();

    let _isMounted = useRef(true);
    const [kernel, setKernel] = useState('')
    const [instanceType, setInstanceType] = useState('ml.t2.medium')

    let userId = useSelector(usernameSelector);
    let editExperimentState = useSelector(editExperimentSelector);
    let fetchedExperimentState = useSelector(fetchedExperimentSelector);

    let showMessage = editExperimentState.showUpdateExperimentDisplayMessage ||
        (fetchedExperimentState.experimentFetched && !fetchedExperimentState.experimentFetchSuccess)

    let isSuccessMessage = editExperimentState.showUpdateExperimentSuccess &&
        fetchedExperimentState.experimentFetchSuccess

    let disableSubmit = !(fetchedExperimentState.experimentFetched && fetchedExperimentState.experimentFetchSuccess) ||
        isEmpty(userId)

    const getMessage = () => {
        if(!fetchedExperimentState.experimentFetchSuccess) {
            return EXPERIMENT_FETCH_FAILED_MESSAGE
        }  else {
            return editExperimentState.updateExperimentMessage
        }
    }

    useEffect(() => {
        _isMounted.current = true;
    });

    useEffect(() => {
        return () => {
            _isMounted.current = false;
        }
    });

    const getExperiment = () => {
        dispatch(loadExperimentResult(params.experimentName))
    }

    useEffect(() => {
        if (_isMounted.current) {
            getExperiment()
        }
    }, [userId, params.teamId, params.experimentName]);

    const onSubmit = (event: any) => {
        event.preventDefault()
        dispatch({type: START_UPDATE_EXPERIMENT_LOADER})
        if (isEmpty(params.experimentName) ||
            isEmpty(kernel)) {
            alert(FIELDS_REQUIRED_MESSAGE)
            return
        }
        dispatch(updateExperiment({
            namespaceId: fetchedExperimentState.experimentData.namespaceId,
            kernel: kernel,
            runwayId: fetchedExperimentState.experimentData.runwayId,
            notebookInstanceName: params.experimentName,
            instanceType: isEmpty(instanceType) ? null : instanceType,
            teamId: params.teamId
        }))
    }

    const handleSelectInstanceType = (event: any) => {
        setIfMounted(_isMounted.current, setInstanceType, event.target.value)
    }

    const handleSelectKernel = (event: any) => {
        setIfMounted(_isMounted.current, setKernel, event.target.value)
    }

    return (
        <div className="EditExperiment">
            {showMessage ?
                <KatFlashbar variant={isSuccessMessage ? "success" : "danger"}
                             header={getMessage()}
                             link-label={isSuccessMessage ? "Track status" : null}
                             linkHref={isSuccessMessage ? `/modelTraining/${params.teamId}/namespaces/${fetchedExperimentState.experimentData.namespaceId}/runways/${fetchedExperimentState.experimentData.runwayId}/experiments` : undefined}>
                </KatFlashbar>
                : editExperimentState.showUpdateExperimentLoading ? <KatSpinner/>: null
            }
            <form className='EditExperiment-form' onSubmit={onSubmit}>
                <FormInput
                    label="Experiment Name *"
                    type='text'
                    placeholder='Experiment name'
                    tooltipText="This is the name of the sagemaker notebook to be created."
                    value={params.experimentName}
                    disabled
                    required
                />
                <br/>
                {/*TODO: Change values in backend instead of replacing EMR with spark.*/}
                <br/>
                <KatLabel tooltipText="This indicates whether the notebook will have spark kernel support or not.
                Experiment with Training indicates support for spark won't be there"> Kernel Use case * :&nbsp;</KatLabel>
                <KatRadiobutton value={KernelType.EMR_KERNEL}
                                label="Spark"
                                checked={fetchedExperimentState.experimentData.kernel==KernelType.EMR_KERNEL}
                                onChange={handleSelectKernel}>Spark</KatRadiobutton>
                <KatRadiobutton value={KernelType.TRAINING_EMR_KERNEL}
                                label="Spark+Training"
                                checked={fetchedExperimentState.experimentData.kernel==KernelType.TRAINING_EMR_KERNEL}
                                onChange={handleSelectKernel}>Spark+Training</KatRadiobutton>
                <KatRadiobutton value={KernelType.TRAINING}
                                checked={fetchedExperimentState.experimentData.kernel==KernelType.TRAINING}
                                label="Training"
                                onChange={handleSelectKernel}>Training</KatRadiobutton>
                <br/>
                <KatLabel tooltipText="Instance type of the notebook instance created.">
                    Notebook Instance Type :&nbsp;
                </KatLabel>
                <FormDropDown value="ml.t2.medium" options={INSTANCE_TYPES} onChange={handleSelectInstanceType}>
                </FormDropDown>
                <br/>
                <KatButton type="submit" label="Update" variant="primary" size="base"
                           disabled={disableSubmit}/>
            </form>
        </div>
    );
}

export default EditExperiment;
